import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, TableSortLabel, IconButton, CircularProgress, Snackbar, Grid,
  Box, Pagination, MenuItem, Paper, Typography, styled, alpha, Chip,
  Tooltip, InputAdornment, Modal, Collapse, Card, CardContent, Badge,
  Switch, FormControlLabel, Divider
} from "@mui/material";
import {
  collection, onSnapshot, updateDoc, doc, query,
  where, getDocs, deleteDoc
} from "firebase/firestore";
import {
  Delete, Edit, Save, Add, Category, Search,
  WarningAmber, KeyboardArrowDown, KeyboardArrowUp,
  Close, DownloadDoneOutlined, Inventory2, TrendingUp,
  Assessment, ShoppingCart, LocalOffer, ExpandMore, ExpandLess
} from "@mui/icons-material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { db } from "../firebase-config";
import AddCategoryModal from "../components/add-category-modal";
import AddProductModal from "../components/inventory-form";
import Collections from "../collections";
import Header from "../components/header";
import InventoryForm from "../components/inventory-form";
import { useAuth } from "../auth-context";
import exportToExcel from "../utils/exportInventory";
import appTheme from "../theme"; // Import the luxury theme
import { fontSize } from "@mui/system";

const SearchField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: appTheme.borderRadius.medium,
    backgroundColor: appTheme.colors.surface,
    boxShadow: appTheme.shadows.whisper,
    "& fieldset": {
      borderColor: appTheme.colors.borderPrimary,
    },
    "&:hover fieldset": {
      borderColor: appTheme.colors.primary,
    },
    "&.Mui-focused fieldset": {
      borderColor: appTheme.colors.primary,
      borderWidth: "2px",
    },
  },
  "& .MuiInputLabel-root": {
    color: appTheme.colors.textSecondary,
    "&.Mui-focused": {
      color: appTheme.colors.primary,
    },
  },
});

// **STYLED COMPONENTS WITH LUXURY THEME**
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: appTheme.borderRadius.rounded,
  boxShadow: appTheme.shadows.soft,
  border: `1px solid ${appTheme.colors.borderSubtle}`,
  transition: appTheme.transitions.luxury,
  "&:hover": {
    boxShadow: appTheme.shadows.medium,
    transform: "translateY(-2px)",
  }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: appTheme.borderRadius.rounded,
  boxShadow: appTheme.shadows.medium,
  border: `1px solid ${appTheme.colors.borderPrimary}`,
  maxHeight: "calc(100vh - 200px)",
  backgroundColor: appTheme.colors.surface,
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  background: appTheme.gradients.cardPremium,
  "& .MuiTableCell-head": {
    color: appTheme.colors.deepTeal, // Pure white for better contrast
    fontWeight: appTheme.typography.fontWeights.bold,
    fontSize: appTheme.typography.fontSizes.small,
    // textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "none",
  }
}));


const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: appTheme.colors.surfaceLuxury,
  },
  "&:hover": {
    backgroundColor: alpha(appTheme.colors.jadeFog, 0.3),
    transform: "scale(1.001)",
    boxShadow: appTheme.shadows.whisper,
  },
  transition: appTheme.transitions.smooth,
}));

const ActionButton = styled(Button)(({ variant, color }) => ({
  borderRadius: appTheme.borderRadius.medium,
  textTransform: "none",
  fontWeight: appTheme.typography.fontWeights.semibold,
  padding: `${appTheme.spacing.sm} ${appTheme.spacing.md}`,
  boxShadow: variant === "contained" ? appTheme.shadows.soft : "none",
  transition: appTheme.transitions.luxury,
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: variant === "contained" ? appTheme.shadows.medium : appTheme.shadows.soft,
  }
}));

const StatCard = styled(Card)(({ theme }) => ({
  borderRadius: appTheme.borderRadius.rounded,
  background: appTheme.gradients.cardLuxury,
  boxShadow: appTheme.shadows.goldGlow,
  transition: appTheme.transitions.luxury,
  border: `1px solid ${appTheme.colors.borderAccent}`,
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: appTheme.shadows.dramatic,
  }
}));

const Inventory = () => {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [orderBy, setOrderBy] = useState("title");
  const [orderDirection, setOrderDirection] = useState("asc");
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [updatedValues, setUpdatedValues] = useState({});
  const [loadingField, setLoadingField] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [initialLoading, setInitialLoading] = useState(false);
  const [totals, setTotals] = useState({
    totalQuantity: 0,
    totalSold: 0,
    totalPrice: 0,
    totalUnitPrice: 0,
    totalVAT: 0,
  });
  const [categories, setCategories] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [currentPage, setCurrentPage] = useState(1);
  const [openItemModal, setOpenItemModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedRows, setExpandedRows] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [inventoryFormOpen, setInventoryFormOpen] = useState(false);
  // **NEW STATE FOR COLLAPSIBLE CARDS**
  const [statsExpanded, setStatsExpanded] = useState(true);


  const { userRole } = useAuth();
  const itemsPerPage = 10;

  // Debounced search function to optimize performance
  const debouncedSetSearchTerm = useMemo(() => {
    const debounce = (func, delay) => {
      let timeoutId;
      return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(null, args), delay);
      };
    };

    return debounce((value) => {
      setSearchTerm(value);
    }, 300); // 300ms delay
  }, []);

  // Handle input change without losing focus
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setInputValue(value); // Update input immediately for responsive UI
    debouncedSetSearchTerm(value); // Update search term after debounce
  }, [debouncedSetSearchTerm]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.cancel && debouncedSetSearchTerm.cancel();
    };
  }, [debouncedSetSearchTerm]);


  // **SAFE NUMBER HELPER**
  const safeToFixed = (value, decimals = 2) => {
    if (value === undefined || value === null || isNaN(value)) return '0.00';
    return Number(value).toFixed(Number);
  };

  const safeNumber = (value) => {
    if (value === undefined || value === null || isNaN(value)) return 0;
    return Number(value);
  };

  // **CALCULATION FUNCTIONS**
  const calculateVATAmount = (price, hasVAT) => {
    return hasVAT ? price * 0.2 : 0;
  };

  const calculatePriceWithVAT = (price, hasVAT) => {
    return hasVAT ? price * 1.2 : price;
  };

  const calculateTotals = (items) => {
    const initialTotals = {
      totalQuantity: 0,
      totalSold: 0,
      totalSellingPrice: 0,
      totalActualPrice: 0,
      totalSellingCost: 0,
      totalActualCost: 0,
      totalVAT: 0,
      lowStockItems: 0,
    };

    if (!items || !Array.isArray(items)) return initialTotals;

    return items.reduce((acc, product) => {
      const availableQuantity = safeNumber(product.availableQuantity);
      const soldQuantity = safeNumber(product.soldQuantity);
      const baseSellingPrice = safeNumber(product.sellingPrice);
      const actualPrice = safeNumber(product.actualPrice);
      const hasVAT = Boolean(product.hasVAT);
      const lowStockThreshold = safeNumber(product.lowStockThreshold) || 5;

      const vatAmount = calculateVATAmount(baseSellingPrice, hasVAT);
      const finalSellingPrice = calculatePriceWithVAT(baseSellingPrice, hasVAT);

      return {
        totalQuantity: acc.totalQuantity + availableQuantity,
        totalSold: acc.totalSold + soldQuantity,
        totalSellingPrice: acc.totalSellingPrice + finalSellingPrice,
        totalActualPrice: acc.totalActualPrice + actualPrice,
        totalSellingCost: acc.totalSellingCost + (availableQuantity * finalSellingPrice),
        totalActualCost: acc.totalActualCost + (availableQuantity * actualPrice),
        totalVAT: acc.totalVAT + (availableQuantity * vatAmount),
        lowStockItems: acc.lowStockItems + (availableQuantity <= lowStockThreshold ? 1 : 0),
      };
    }, initialTotals);
  };

  // **EVENT HANDLERS**
  const handleOpenModal = (item = null) => {
    setSelectedItem(item);
    setOpenItemModal(true);
  };

  const handleCloseModal = () => {
    setOpenItemModal(false);
    setSelectedItem(null);
  };

  const handleSubmitSuccess = (savedItem) => {
    setSnackbarMessage(
      savedItem.id === editingProduct?.id
        ? "Product updated successfully!"
        : "Product created successfully!"
    );
    setSnackbarOpen(true);
    setEditingProduct(null);
    setInventoryFormOpen(false);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      const inventoryQuery = query(
        collection(db, Collections.INVENTORY_ITEMS),
        where("id", "==", itemToDelete.id)
      );
      const querySnapshot = await getDocs(inventoryQuery);

      if (querySnapshot.empty) {
        setSnackbarMessage("Item not found!");
        setSnackbarOpen(true);
        return;
      }

      const docId = querySnapshot.docs[0].id;
      const itemRef = doc(db, Collections.INVENTORY_ITEMS, docId);
      await deleteDoc(itemRef);

      setProducts((prevProducts) =>
        prevProducts.filter((product) => product.id !== itemToDelete.id)
      );

      setSnackbarMessage("Item deleted successfully!");
      setSnackbarOpen(true);
      setItemToDelete(null);
    } catch (error) {
      console.error("Error deleting document: ", error);
      setSnackbarMessage("Error deleting item.");
      setSnackbarOpen(true);
    } finally {
      setDeleteConfirmOpen(false);
    }
  };

  // **USE EFFECTS**
  useEffect(() => {
    setTotals(calculateTotals(products));
  }, [products]);

  useEffect(() => {
    setInitialLoading(true);

    const fetchCategories = async () => {
      try {
        const categorySnapshot = await getDocs(
          collection(db, Collections.INVENTORY_CATEGORY)
        );
        const categoriesData = categorySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCategories(categoriesData);
      } catch (error) {
        console.error("Error fetching categories: ", error);
      }
    };

    fetchCategories();

    const unsubscribe = onSnapshot(
      collection(db, Collections.INVENTORY_ITEMS),
      (snapshot) => {
        const updatedItems = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProducts(updatedItems);
        setInitialLoading(false);
      },
      (error) => {
        console.error("Error fetching data: ", error);
        setInitialLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const getCategoryName = (id) =>
    categories.find((cat) => cat.id === id)?.category || "";

  // **FILTERING AND SORTING**
  // Memoized filtering for better performance
  const filteredProducts = useMemo(() => {
    if (!searchTerm.trim()) {
      return products; // Return all products if no search term
    }

    const searchQuery = searchTerm.toLowerCase().trim();

    return products.filter((product) => {
      // Safe string comparison
      const productTitle = (product.title || "").toLowerCase();
      const categoryName = getCategoryName(product.categoryId);
      const categoryLower = (categoryName || "").toLowerCase();

      // Search in both title and category
      return productTitle.includes(searchQuery) ||
        categoryLower.includes(searchQuery);
    });
  }, [products, searchTerm, categories]); // Dependencies for memoization


  const sortedProducts = [...filteredProducts].sort((a, b) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    if (orderBy === "category") {
      aValue = getCategoryName(a.categoryId);
      bValue = getCategoryName(b.categoryId);
    }
    if (typeof aValue === "string") aValue = aValue.toLowerCase();
    if (typeof bValue === "string") bValue = bValue.toLowerCase();

    if (orderDirection === "asc") {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const paginatedProducts = sortedProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (column) => {
    const isAsc = orderBy === column && orderDirection === "asc";
    setOrderBy(column);
    setOrderDirection(isAsc ? "desc" : "asc");
  };

  const handlePageChange = (_, newPage) => {
    setCurrentPage(newPage);
  };

  return (
    <Box sx={{
      padding: appTheme.spacing.lg,
      backgroundColor: appTheme.colors.surfaceLuxury,
      minHeight: "100vh"
    }}>
      {/* **HEADER SECTION WITH COLLAPSIBLE BUTTON** */}
      <Box sx={{
        marginBottom: appTheme.spacing.xl,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start"
      }}>
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: appTheme.typography.fontWeights.black,
              color: appTheme.colors.primary,
              marginBottom: appTheme.spacing.xs,
              background: appTheme.gradients.hero,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            📊 Inventory Management
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: appTheme.colors.textSecondary,
              marginBottom: appTheme.spacing.lg,
              fontWeight: appTheme.typography.fontWeights.regular
            }}
          >
            Manage your restaurant inventory with advanced analytics
          </Typography>
        </Box>

        {/* **COLLAPSIBLE STATS BUTTON** */}
        <ActionButton
          variant="outlined"
          onClick={() => setStatsExpanded(!statsExpanded)}
          startIcon={statsExpanded ? <ExpandLess /> : <ExpandMore />}
          sx={{
            borderColor: appTheme.colors.accent,
            color: appTheme.colors.accent,
            backgroundColor: appTheme.colors.surface,
            "&:hover": {
              backgroundColor: alpha(appTheme.colors.accent, 0.1),
              borderColor: appTheme.colors.accent,
            }
          }}
        >
          {statsExpanded ? "Hide Stats" : "Show Stats"}
        </ActionButton>
      </Box>

      {/* **COLLAPSIBLE STATISTICS CARDS** */}
      {/* **FIXED COLLAPSIBLE STATISTICS CARDS** */}
      <Collapse in={statsExpanded} timeout={500}>
        <Grid container spacing={3} sx={{ marginBottom: appTheme.spacing.xl }}>
          {/* Total Items Card */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard sx={{
              background: appTheme.gradients.cardLuxury,
              height: "100%", // Ensure all cards have same height
              minHeight: { xs: "120px", sm: "140px" }, // Set minimum height
            }}>
              <CardContent sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                padding: { xs: appTheme.spacing.md, sm: appTheme.spacing.lg },
                height: "100%",
                boxSizing: "border-box"
              }}>
                <Box sx={{
                  backgroundColor: appTheme.colors.primary,
                  borderRadius: appTheme.borderRadius.rounded,
                  padding: { xs: appTheme.spacing.sm, sm: appTheme.spacing.md },
                  color: appTheme.colors.champagne,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: { xs: "50px", sm: "60px" },
                  minHeight: { xs: "50px", sm: "60px" },
                }}>
                  <Inventory2 sx={{
                    fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2rem" }
                  }} />
                </Box>
                <Box sx={{
                  minWidth: 0,
                  flex: 1,
                  textAlign: { xs: "center", sm: "left" }
                }}>
                  <Typography variant="h4" sx={{
                    fontWeight: appTheme.typography.fontWeights.bold,
                    color: appTheme.colors.primary,
                    fontSize: {
                      xs: "1.5rem",
                      sm: "1.75rem",
                      md: "2rem",
                      lg: "2.125rem"
                    },
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}>
                    {totals.totalQuantity?.toLocaleString() || 0}
                  </Typography>
                  <Typography variant="body2" sx={{
                    color: appTheme.colors.textSecondary,
                    fontWeight: appTheme.typography.fontWeights.medium,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    lineHeight: 1.3,
                    mt: 0.5
                  }}>
                    Total Items in Stock
                  </Typography>
                </Box>
              </CardContent>
            </StatCard>
          </Grid>

          {/* Inventory Value Card */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard sx={{
              background: appTheme.gradients.cardPremium,
              height: "100%",
              minHeight: { xs: "120px", sm: "140px" }
            }}>
              <CardContent sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                padding: { xs: appTheme.spacing.md, sm: appTheme.spacing.lg },
                height: "100%",
                boxSizing: "border-box"
              }}>
                <Box sx={{
                  backgroundColor: appTheme.colors.secondary,
                  borderRadius: appTheme.borderRadius.rounded,
                  padding: { xs: appTheme.spacing.sm, sm: appTheme.spacing.md },
                  color: appTheme.colors.champagne,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: { xs: "50px", sm: "60px" },
                  minHeight: { xs: "50px", sm: "60px" }
                }}>
                  <TrendingUp sx={{
                    fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2rem" }
                  }} />
                </Box>
                <Box sx={{
                  minWidth: 0,
                  flex: 1,
                  textAlign: { xs: "center", sm: "left" }
                }}>
                  <Typography variant="h4" sx={{
                    fontWeight: appTheme.typography.fontWeights.bold,
                    color: appTheme.colors.secondary,
                    fontSize: {
                      xs: "1.5rem",
                      sm: "1.75rem",
                      md: "2rem",
                      lg: "2.125rem"
                    },
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}>
                    £{safeToFixed(totals?.totalSellingCost)}
                  </Typography>
                  <Typography variant="body2" sx={{
                    color: appTheme.colors.textSecondary,
                    fontWeight: appTheme.typography.fontWeights.medium,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    lineHeight: 1.3,
                    mt: 0.5
                  }}>
                    Total Inventory Value
                  </Typography>
                </Box>
              </CardContent>
            </StatCard>
          </Grid>

          {/* Low Stock Alerts Card */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard sx={{
              background: `linear-gradient(135deg, ${appTheme.colors.goldenHour} 0%, ${appTheme.colors.rosePetal} 100%)`,
              height: "100%",
              minHeight: { xs: "120px", sm: "140px" }
            }}>
              <CardContent sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                padding: { xs: appTheme.spacing.md, sm: appTheme.spacing.lg },
                height: "100%",
                boxSizing: "border-box"
              }}>
                <Box sx={{
                  backgroundColor: appTheme.colors.warning,
                  borderRadius: appTheme.borderRadius.rounded,
                  padding: { xs: appTheme.spacing.sm, sm: appTheme.spacing.md },
                  color: appTheme.colors.surface,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: { xs: "50px", sm: "60px" },
                  minHeight: { xs: "50px", sm: "60px" }
                }}>
                  <WarningAmber sx={{
                    fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2rem" }
                  }} />
                </Box>
                <Box sx={{
                  minWidth: 0,
                  flex: 1,
                  textAlign: { xs: "center", sm: "left" }
                }}>
                  <Typography variant="h4" sx={{
                    fontWeight: appTheme.typography.fontWeights.bold,
                    color: appTheme.colors.warning,
                    fontSize: {
                      xs: "1.5rem",
                      sm: "1.75rem",
                      md: "2rem",
                      lg: "2.125rem"
                    },
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}>
                    {totals.lowStockItems?.toLocaleString() || 0}
                  </Typography>
                  <Typography variant="body2" sx={{
                    color: appTheme.colors.textSecondary,
                    fontWeight: appTheme.typography.fontWeights.medium,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    lineHeight: 1.3,
                    mt: 0.5
                  }}>
                    Low Stock Alerts
                  </Typography>
                </Box>
              </CardContent>
            </StatCard>
          </Grid>

          {/* VAT Collected Card */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard sx={{
              background: `linear-gradient(135deg, ${appTheme.colors.accent} 0%, ${appTheme.colors.goldenHour} 100%)`,
              height: "100%",
              minHeight: { xs: "120px", sm: "140px" }
            }}>
              <CardContent sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                padding: { xs: appTheme.spacing.md, sm: appTheme.spacing.lg },
                height: "100%",
                boxSizing: "border-box"
              }}>
                <Box sx={{
                  backgroundColor: appTheme.colors.accent,
                  borderRadius: appTheme.borderRadius.rounded,
                  padding: { xs: appTheme.spacing.sm, sm: appTheme.spacing.md },
                  color: appTheme.colors.charcoal,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: { xs: "50px", sm: "60px" },
                  minHeight: { xs: "50px", sm: "60px" }
                }}>
                  <LocalOffer sx={{
                    fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2rem" }
                  }} />
                </Box>
                <Box sx={{
                  minWidth: 0,
                  flex: 1,
                  textAlign: { xs: "center", sm: "left" }
                }}>
                  <Typography variant="h4" sx={{
                    fontWeight: appTheme.typography.fontWeights.bold,
                    color: appTheme.colors.accent,
                    fontSize: {
                      xs: "1.5rem",
                      sm: "1.75rem",
                      md: "2rem",
                      lg: "2.125rem"
                    },
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}>
                    £{safeToFixed(totals?.totalVAT)}
                  </Typography>
                  <Typography variant="body2" sx={{
                    color: appTheme.colors.textSecondary,
                    fontWeight: appTheme.typography.fontWeights.medium,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    lineHeight: 1.3,
                    mt: 0.5
                  }}>
                    Total VAT Collected
                  </Typography>
                </Box>
              </CardContent>
            </StatCard>
          </Grid>
        </Grid>
      </Collapse>

      {/* **CONTROLS SECTION** */}
      <StyledCard sx={{ marginBottom: appTheme.spacing.lg, padding: appTheme.spacing.lg }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <SearchField
              key="inventory-search"
              fullWidth
              label="Search products by title or category"
              variant="outlined"
              value={inputValue} // Use inputValue for immediate response
              onChange={handleInputChange} // Use optimized handler
              placeholder="Type to search products..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: appTheme.colors.textSecondary }} />
                  </InputAdornment>
                ),
              }}
            />

          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", gap: 2, justifyContent: { xs: "center", md: "flex-end" } }}>
              <ActionButton
                variant="outlined"
                onClick={() => exportToExcel()}
                startIcon={<DownloadDoneOutlined />}
                sx={{
                  borderColor: appTheme.colors.primary,
                  color: appTheme.colors.primary,
                  "&:hover": {
                    backgroundColor: alpha(appTheme.colors.primary, 0.1),
                  }
                }}
              >
                Export Data
              </ActionButton>

              <ActionButton
                variant="contained"
                onClick={() => setAddCategoryOpen(true)}
                startIcon={<Category />}
                sx={{
                  backgroundColor: appTheme.colors.primary,
                  "&:hover": {
                    backgroundColor: appTheme.colors.deepTeal,
                  }
                }}
              >
                Add Category
              </ActionButton>

              <ActionButton
                variant="contained"
                onClick={() => setOpenItemModal(true)}
                startIcon={<Add />}
                sx={{
                  backgroundColor: appTheme.colors.secondary,
                  "&:hover": {
                    backgroundColor: appTheme.colors.warmCopper,
                  }
                }}
              >
                Add Product
              </ActionButton>
            </Box>
          </Grid>
        </Grid>
      </StyledCard>

      {/* **ENHANCED TABLE WITH REORDERED COLUMNS** */}
      <StyledTableContainer component={Paper}>
        <Table stickyHeader>
          <StyledTableHead>
            <TableRow>
              <TableCell sx={{ width: "40px" }}></TableCell>

              <TableCell>
                <TableSortLabel
                  active={orderBy === "title"}
                  direction={orderBy === "title" ? orderDirection : "asc"}
                  onClick={() => handleSort("title")}
                  sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { color: "inherit !important" } }}
                >
                  Product Name
                </TableSortLabel>
              </TableCell>

              <TableCell>
                <TableSortLabel
                  active={orderBy === "brand"}
                  direction={orderBy === "brand" ? orderDirection : "asc"}
                  onClick={() => handleSort("brand")}
                  sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { color: "inherit !important" } }}
                >
                  Brand
                </TableSortLabel>
              </TableCell>

              <TableCell>
                <TableSortLabel
                  active={orderBy === "vendor"}
                  direction={orderBy === "vendor" ? orderDirection : "asc"}
                  onClick={() => handleSort("vendor")}
                  sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { color: "inherit !important" } }}
                >
                  Vendor
                </TableSortLabel>
              </TableCell>

              <TableCell>
                <TableSortLabel
                  active={orderBy === "category"}
                  direction={orderBy === "category" ? orderDirection : "asc"}
                  onClick={() => handleSort("category")}
                  sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { color: "inherit !important" } }}
                >
                  Category
                </TableSortLabel>
              </TableCell>

              <TableCell>
                <TableSortLabel
                  active={orderBy === "units"}
                  direction={orderBy === "units" ? orderDirection : "asc"}
                  onClick={() => handleSort("units")}
                  sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { color: "inherit !important" } }}
                >
                  Units
                </TableSortLabel>
              </TableCell>

              <TableCell>
                <TableSortLabel
                  active={orderBy === "availableQuantity"}
                  direction={orderBy === "availableQuantity" ? orderDirection : "asc"}
                  onClick={() => handleSort("availableQuantity")}
                  sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { color: "inherit !important" } }}
                >
                  Stock Qty
                </TableSortLabel>
              </TableCell>

              <TableCell>
                <TableSortLabel
                  active={orderBy === "actualPrice"}
                  direction={orderBy === "actualPrice" ? orderDirection : "asc"}
                  onClick={() => handleSort("actualPrice")}
                  sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { color: "inherit !important" } }}
                >
                  Actual Price
                </TableSortLabel>
              </TableCell>

              <TableCell>
                <TableSortLabel
                  active={orderBy === "sellingPrice"}
                  direction={orderBy === "sellingPrice" ? orderDirection : "asc"}
                  onClick={() => handleSort("sellingPrice")}
                  sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { color: "inherit !important" } }}
                >
                  Selling Price
                </TableSortLabel>
              </TableCell>

              <TableCell>
                <TableSortLabel
                  active={orderBy === "soldQuantity"}
                  direction={orderBy === "soldQuantity" ? orderDirection : "asc"}
                  onClick={() => handleSort("soldQuantity")}
                  sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { color: "inherit !important" } }}
                >
                  Stock Out
                </TableSortLabel>
              </TableCell>

              <TableCell>
                <TableSortLabel
                  active={orderBy === "lowStockThreshold"}
                  direction={orderBy === "lowStockThreshold" ? orderDirection : "asc"}
                  onClick={() => handleSort("lowStockThreshold")}
                  sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { color: "inherit !important" } }}
                >
                  Low Stock Alert
                </TableSortLabel>
              </TableCell>

              <TableCell>
                <TableSortLabel
                  active={orderBy === "orderingThreshold"}
                  direction={orderBy === "orderingThreshold" ? orderDirection : "asc"}
                  onClick={() => handleSort("orderingThreshold")}
                  sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { color: "inherit !important" } }}
                >
                  Order Threshold
                </TableSortLabel>
              </TableCell>

              <TableCell>Total Cost (Actual)</TableCell> {/* Don't sort calculated values */}
              <TableCell>Total Cost (Sell)</TableCell> {/* Don't sort calculated values */}

              <TableCell>
                <TableSortLabel
                  active={orderBy === "updatedAt"}
                  direction={orderBy === "updatedAt" ? orderDirection : "asc"}
                  onClick={() => handleSort("updatedAt")}
                  sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { color: "inherit !important" } }}
                >
                  Updated
                </TableSortLabel>
              </TableCell>

              <TableCell>Actions</TableCell> {/* Don't sort actions */}
            </TableRow>
          </StyledTableHead>


          {initialLoading ? (
            <TableBody>
              <TableRow>
                <TableCell colSpan={15} align="center" sx={{ padding: appTheme.spacing.huge }}>
                  <CircularProgress size={50} sx={{ color: appTheme.colors.primary }} />
                  <Typography sx={{
                    marginTop: 2,
                    color: appTheme.colors.textSecondary,
                    fontWeight: appTheme.typography.fontWeights.medium
                  }}>
                    Loading inventory...
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody>
              {paginatedProducts.map((product, index) => {
                const lowStockThreshold = Number(product.lowStockThreshold) || 5;
                const isLowStock = Number(product.availableQuantity) <= lowStockThreshold;
                const hasVAT = product.hasVAT || false;
                const finalSellingPrice = calculatePriceWithVAT(Number(product.sellingPrice) || 0, hasVAT);

                return (
                  <React.Fragment key={product.id}>
                    <StyledTableRow>
                      <TableCell>
                        {userRole === "superadmin" && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setExpandedRows((prev) =>
                                prev.includes(product.id)
                                  ? prev.filter((id) => id !== product.id)
                                  : [...prev, product.id]
                              );
                            }}
                            sx={{ color: appTheme.colors.primary }}
                          >
                            {expandedRows.includes(product.id) ? (
                              <KeyboardArrowUp />
                            ) : (
                              <KeyboardArrowDown />
                            )}
                          </IconButton>
                        )}
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" sx={{
                          fontWeight: appTheme.typography.fontWeights.semibold,
                          color: appTheme.colors.textPrimary
                        }}>
                          {product.title}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" sx={{ color: appTheme.colors.textSecondary }}>
                          {product.brand}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" sx={{ color: appTheme.colors.textSecondary }}>
                          {product.vendor}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={getCategoryName(product.categoryId) || "N/A"}
                          size="small"
                          sx={{
                            backgroundColor: alpha(appTheme.colors.primary, 0.1),
                            color: appTheme.colors.primary,
                            fontWeight: appTheme.typography.fontWeights.semibold,
                            borderRadius: appTheme.borderRadius.soft,
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" sx={{ color: appTheme.colors.textSecondary }}>
                          {product.units}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: appTheme.typography.fontWeights.semibold,
                              color: isLowStock ? appTheme.colors.error : appTheme.colors.textPrimary,
                            }}
                          >
                            {product.availableQuantity}
                          </Typography>
                          {isLowStock && (
                            <Tooltip title={`Low stock! Threshold: ${lowStockThreshold}`} arrow>
                              <WarningAmber
                                fontSize="small"
                                sx={{ color: appTheme.colors.error }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" sx={{
                          fontWeight: appTheme.typography.fontWeights.medium,
                          color: appTheme.colors.textPrimary
                        }}>
                          £{safeToFixed(product?.actualPrice)}
                        </Typography>
                      </TableCell>

                      {/* **SELLING PRICE INCLUDING VAT** */}
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{
                            fontWeight: appTheme.typography.fontWeights.semibold,
                            color: hasVAT ? appTheme.colors.secondary : appTheme.colors.textPrimary
                          }}>
                            £{safeToFixed(finalSellingPrice)}
                          </Typography>
                          {hasVAT && (
                            <Chip
                              label="VAT"
                              size="small"
                              sx={{
                                backgroundColor: alpha(appTheme.colors.accent, 0.2),
                                color: appTheme.colors.accent,
                                fontSize: "0.6rem",
                                height: "16px",
                                marginTop: "2px",
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" sx={{ color: appTheme.colors.textSecondary }}>
                          {product.soldQuantity || 0}
                        </Typography>
                      </TableCell>

                      {/* **MOVED COLUMNS: LOW STOCK ALERT** */}
                      <TableCell>
                        <Chip
                          label={`${lowStockThreshold} units`}
                          size="small"
                          sx={{
                            backgroundColor: isLowStock
                              ? alpha(appTheme.colors.error, 0.1)
                              : alpha(appTheme.colors.success, 0.1),
                            color: isLowStock ? appTheme.colors.error : appTheme.colors.success,
                            fontWeight: appTheme.typography.fontWeights.semibold,
                          }}
                        />
                      </TableCell>

                      {/* **MOVED COLUMNS: ORDER THRESHOLD** */}
                      <TableCell>
                        <Typography variant="body2" sx={{
                          fontWeight: appTheme.typography.fontWeights.medium,
                          color: appTheme.colors.textSecondary
                        }}>
                          {product.orderingThreshold || 0}/month
                        </Typography>
                      </TableCell>

                      {/* **TOTAL COSTS** */}
                      <TableCell>
                        <Typography variant="body2" sx={{
                          fontWeight: appTheme.typography.fontWeights.semibold,
                          color: appTheme.colors.textPrimary
                        }}>
                          £{safeToFixed((product.availableQuantity || 0) * (product.actualPrice || 0))}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" sx={{
                          fontWeight: appTheme.typography.fontWeights.semibold,
                          color: appTheme.colors.secondary
                        }}>
                          £{safeToFixed((product.availableQuantity || 0) * finalSellingPrice)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: appTheme.typography.fontSizes.small,
                            color: appTheme.colors.textMuted,
                          }}
                        >
                          {product.updatedAt
                            ? new Date(product.updatedAt).toLocaleDateString("en-GB")
                            : product.createdAt
                              ? new Date(product.createdAt).toLocaleDateString("en-GB")
                              : "N/A"}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title="Edit product">
                            <IconButton
                              onClick={() => handleOpenModal(product)}
                              size="small"
                              sx={{
                                color: appTheme.colors.primary,
                                "&:hover": {
                                  backgroundColor: alpha(appTheme.colors.primary, 0.1),
                                },
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete product">
                            <IconButton
                              onClick={() => {
                                setItemToDelete(product);
                                setDeleteConfirmOpen(true);
                              }}
                              size="small"
                              sx={{
                                color: appTheme.colors.error,
                                "&:hover": {
                                  backgroundColor: alpha(appTheme.colors.error, 0.1),
                                },
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </StyledTableRow>

                    {/* **EXPANDED ROW FOR INGREDIENTS** */}
                    {expandedRows.includes(product.id) && (
                      <TableRow
                        sx={{
                          backgroundColor: alpha(appTheme.colors.jadeFog, 0.3),
                        }}
                      >
                        <TableCell colSpan={15} sx={{ py: 0 }}>
                          <Collapse
                            in={expandedRows.includes(product.id)}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={{ margin: 2 }}>
                              <Typography
                                variant="h6"
                                gutterBottom
                                sx={{
                                  color: appTheme.colors.primary,
                                  fontWeight: appTheme.typography.fontWeights.bold
                                }}
                              >
                                🧩 Ingredients Breakdown
                              </Typography>
                              {product.ingredients && product.ingredients.length > 0 ? (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: appTheme.typography.fontWeights.semibold }}>Ingredient</TableCell>
                                      <TableCell sx={{ fontWeight: appTheme.typography.fontWeights.semibold }}>Qty Per Unit</TableCell>
                                      <TableCell sx={{ fontWeight: appTheme.typography.fontWeights.semibold }}>Total Used</TableCell>
                                      <TableCell sx={{ fontWeight: appTheme.typography.fontWeights.semibold }}>Units</TableCell>
                                      <TableCell sx={{ fontWeight: appTheme.typography.fontWeights.semibold }}>Actual Price</TableCell>
                                      <TableCell sx={{ fontWeight: appTheme.typography.fontWeights.semibold }}>Selling Price</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {product.ingredients.map((ingredient) => (
                                      <TableRow key={ingredient.id}>
                                        <TableCell>{ingredient.title}</TableCell>
                                        <TableCell>{ingredient.quantityPerUnit}</TableCell>
                                        <TableCell>{ingredient.totalQuantityUsed}</TableCell>
                                        <TableCell>{ingredient.units}</TableCell>
                                        <TableCell>£{safeToFixed(ingredient?.actualPrice)}</TableCell>
                                        <TableCell>£{safeToFixed(ingredient?.sellingPrice)}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: appTheme.colors.textMuted,
                                    fontStyle: "italic",
                                  }}
                                >
                                  No ingredients configured for this product
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}

              {/* **UPDATED TOTALS ROW WITH PROPER ALIGNMENT** */}
              <TableRow sx={{
                background: appTheme.gradients.hero,
                position: 'sticky',
                bottom: 0,
                zIndex: 10
              }}>
                <TableCell colSpan={6} sx={{
                  color: appTheme.colors.champagne,
                  fontWeight: appTheme.typography.fontWeights.bold,
                  fontSize: appTheme.typography.fontSizes.medium
                }}>
                  📊 INVENTORY TOTALS
                </TableCell>
                <TableCell sx={{ color: appTheme.colors.champagne, fontWeight: appTheme.typography.fontWeights.bold }}>
                  {totals.totalQuantity}
                </TableCell>
                <TableCell sx={{ color: appTheme.colors.champagne, fontWeight: appTheme.typography.fontWeights.bold }}>
                  £{safeToFixed(totals.totalActualPrice)}
                </TableCell>
                <TableCell sx={{ color: appTheme.colors.champagne, fontWeight: appTheme.typography.fontWeights.bold }}>
                  £{safeToFixed(totals.totalSellingPrice)}
                </TableCell>
                <TableCell sx={{ color: appTheme.colors.champagne, fontWeight: appTheme.typography.fontWeights.bold }}>
                  {totals.totalSold}
                </TableCell>
                <TableCell sx={{ color: appTheme.colors.champagne, fontWeight: appTheme.typography.fontWeights.bold }}>
                  {totals.lowStockItems} Items
                </TableCell>
                <TableCell></TableCell> {/* Order Threshold - no total */}
                <TableCell sx={{ color: appTheme.colors.champagne, fontWeight: appTheme.typography.fontWeights.bold }}>
                  £{safeToFixed(totals.totalActualCost)}
                </TableCell>
                <TableCell sx={{ color: appTheme.colors.champagne, fontWeight: appTheme.typography.fontWeights.bold }}>
                  £{safeToFixed(totals.totalSellingCost)}
                </TableCell>
                <TableCell colSpan={2}></TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>

        {/* **ENHANCED PAGINATION** */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: appTheme.spacing.md,
            // backgroundColor: appTheme.colors.surfaceElevated,
          }}
        >
          <Pagination
            count={Math.ceil(sortedProducts.length / itemsPerPage)}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: appTheme.borderRadius.medium,
                fontWeight: appTheme.typography.fontWeights.semibold,
                "&.Mui-selected": {
                  background: appTheme.gradients.hero,
                  color: appTheme.colors.champagne,
                },
              },
            }}
          />
        </Box>
      </StyledTableContainer>

      {/* **ENHANCED MODALS** */}
      <Modal
        open={openItemModal}
        onClose={handleCloseModal}
        aria-labelledby="inventory-modal"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: "95%",
            maxWidth: "900px",
            height: "90vh",
            bgcolor: "background.paper",
            borderRadius: appTheme.borderRadius.rounded,
            boxShadow: appTheme.shadows.dramatic,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <InventoryForm
            editItem={selectedItem}
            onSubmitSuccess={handleSubmitSuccess}
            onClose={handleCloseModal}
          />
        </Box>
      </Modal>

      {/* **ENHANCED DELETE DIALOG** */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: appTheme.borderRadius.rounded,
            padding: appTheme.spacing.xs,
          }
        }}
      >
        <DialogTitle sx={{
          color: appTheme.colors.error,
          fontWeight: appTheme.typography.fontWeights.bold,
          display: "flex",
          alignItems: "center",
          gap: 1
        }}>
          <Delete /> Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: appTheme.typography.fontSizes.base }}>
            Are you sure you want to delete "{itemToDelete?.title}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: appTheme.spacing.md }}>
          <ActionButton onClick={() => setDeleteConfirmOpen(false)}>
            Cancel
          </ActionButton>
          <ActionButton
            onClick={handleDelete}
            variant="contained"
            sx={{ backgroundColor: appTheme.colors.error }}
          >
            Delete Product
          </ActionButton>
        </DialogActions>
      </Dialog>

      <AddCategoryModal
        open={addCategoryOpen}
        onClose={() => setAddCategoryOpen(false)}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        sx={{
          "& .MuiSnackbarContent-root": {
            backgroundColor: appTheme.colors.success,
            borderRadius: appTheme.borderRadius.medium,
            fontSize: appTheme.typography.fontSizes.base,
            fontWeight: appTheme.typography.fontWeights.semibold,
          }
        }}
      />
    </Box>
  );
};

export default Inventory;
